import React, { memo, ReactNode, useCallback } from 'react';
import { propOr } from 'ramda';
import { Box, Collapse, Paper, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Icon, AlyceTheme, IconProp } from '@alycecom/ui';

export interface ICloseableTabProps {
  isOpen: boolean;
  toggleOpen: (name: string, isOpen: boolean) => void;
  children?: ReactNode;
  title: string;
  name?: string;
  icon?: IconProp;
  border?: {
    width?: number;
    color?: keyof AlyceTheme['palette'];
  };
}

const useStyles = makeStyles<AlyceTheme>(({ palette, spacing }) => ({
  tab: ({ border }: Pick<ICloseableTabProps, 'border'>) => ({
    marginTop: spacing(3),
    width: '100%',
    paddingTop: 1,
    paddingBottom: 1,
    border: border
      ? `${propOr(1, 'width', border)}px solid ${border.color ? palette[border.color] : palette.primary.main}`
      : 'none',
  }),
  tabHeader: {
    height: 24,
    cursor: 'pointer',
  },
  descriptionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: palette.text.primary,
  },
}));

const CloseableTab = ({ name = '', title = '', children, icon, isOpen, border, toggleOpen }: ICloseableTabProps) => {
  const classes = useStyles({ border });
  const tabIcon = isOpen ? 'angle-up' : 'angle-down';
  const handleToggleTab = useCallback(() => toggleOpen(name, isOpen), [name, isOpen, toggleOpen]);
  return (
    <Paper elevation={1} className={classes.tab}>
      <Box
        className={classes.tabHeader}
        m={3}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={handleToggleTab}
      >
        <Typography className={classes.descriptionTitle}>{title}</Typography>
        <Icon icon={(icon as IconProp) || tabIcon} />
      </Box>
      <Collapse in={isOpen} unmountOnExit addEndListener={undefined}>
        {children}
      </Collapse>
    </Paper>
  );
};

export default memo(CloseableTab);
